import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { useAuth } from '../../../features/auth/hooks/useAuth';
import { submitFeedback, FeedbackType } from '../../services/feedbackApi';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const { session, sessionEstablishing } = useAuth();
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('Feedback');
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFeedbackType('Feedback');
      setFeedbackText('');
      setIsSubmitting(false);
      setShowThankYou(false);
      setError(null);
    }
  }, [isOpen]);

  // Handle modal close - prevent closing during submission or thank you message
  const handleClose = () => {
    if (!isSubmitting && !showThankYou) {
      onClose();
    }
  };

  // Auto-close after showing thank you message
  useEffect(() => {
    if (showThankYou) {
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showThankYou, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate both fields are populated
    if (!feedbackType || !feedbackText.trim()) {
      return;
    }

    // Wait for session to stabilize if it's being established
    // But allow submission even without session (for non-authenticated users)
    if (sessionEstablishing) {
      setError('Please wait for authentication to complete');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    
    try {
      // Pass token only if user is authenticated
      // Backend will use 'non-user' as user_id if no token is provided
      const token = session?.access_token;
      await submitFeedback(feedbackType, feedbackText, token);
      setIsSubmitting(false);
      setShowThankYou(true);
    } catch (err) {
      setIsSubmitting(false);
      setError(err instanceof Error ? err.message : 'Failed to submit feedback. Please try again.');
    }
  };

  const isFormValid = feedbackType && feedbackText.trim().length > 0;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Feedback" size="md">
      <div>
        {showThankYou ? (
          <div className="text-center py-8">
            <div className="text-green-500 mb-4">
              <svg
                className="mx-auto h-12 w-12"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="text-lg font-medium text-gray-900">Thanks for your feedback!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="feedback-type" className="block text-sm font-medium text-gray-700 mb-2">
                Type
              </label>
              <select
                id="feedback-type"
                value={feedbackType}
                onChange={(e) => setFeedbackType(e.target.value as FeedbackType)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                required
              >
                <option value="Feedback">Feedback</option>
                <option value="Report a bug">Report a bug</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <textarea
                id="feedback-text"
                rows={4}
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                placeholder="Please share your thoughts..."
                required
              />
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

